import React, { useMemo } from "react";
import {
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  XCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { ActivityTableConfigurator } from "../../components/reports/ActivityTableConfigurator";
import { OverflowInfoCell } from "./OverflowInfoCell";
import { SearchableFilterSelect } from "./SearchableFilterSelect";
import { SortIcon } from "./SortIcon";
import { useVirtualizedRows } from "./hooks/useVirtualizedRows";

const renderActivityHeaderLabel = (label: string) => {
  const normalizedLabel = label.trim().toLocaleLowerCase();
  let shouldWrapToTwoLines = false;

  const normalizedAsciiLabel = normalizedLabel
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  const isTimeLabel =
    normalizedAsciiLabel.includes("saati") ||
    normalizedAsciiLabel.includes("time") ||
    normalizedAsciiLabel.includes("zeit");
  const isStartOrEndLabel =
    normalizedAsciiLabel.includes("baslangic") ||
    normalizedAsciiLabel.includes("bitis") ||
    normalizedAsciiLabel.includes("start") ||
    normalizedAsciiLabel.includes("end") ||
    normalizedAsciiLabel.includes("beginn") ||
    normalizedAsciiLabel.includes("ende");
  shouldWrapToTwoLines = isTimeLabel && isStartOrEndLabel;

  if (!shouldWrapToTwoLines) {
    return <span className="leading-tight whitespace-nowrap">{label}</span>;
  }

  const words = label.trim().split(/\s+/).filter(Boolean);
  if (words.length <= 1) {
    return <span className="leading-tight">{label}</span>;
  }

  return (
    <span className="inline-flex flex-col leading-tight whitespace-normal">
      <span>{words[0]}</span>
      <span>{words.slice(1).join(" ")}</span>
    </span>
  );
};

const isActivityNumericColumn = (column: any) =>
  column?.kind === "default" &&
  (column.id === "duration" || column.id === "cost");

export const ActivitiesTab: React.FC<any> = ({
  isTasksTab = false,
  showPendingApprovalStatusOption = true,
  useSingleDateFilter = false,
  loading,
  searchQuery,
  setSearchQuery,
  statusFilter,
  setStatusFilter,
  completedCount,
  pendingApprovalCount,
  pendingCount,
  rejectedCount,
  taskActivitiesCount,
  taskActivitiesFilter,
  workflowText,
  workflowFilter,
  setWorkflowFilter,
  uniqueWorkflows,
  selectedProject,
  setSelectedProject,
  uniqueProjects,
  activityFilter,
  setActivityFilter,
  uniqueActivities,
  assigneeFilter,
  setAssigneeFilter,
  uniqueAssignees,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  hasActiveFilters,
  clearAllFilters,
  tableCardRef,
  tableCardStyle,
  isActivityTableConfiguratorOpen,
  activityVisibleColumns,
  activityDefaultColumns,
  activityAvailableFormColumns,
  isSavingActivityTableLayout,
  setIsActivityTableConfiguratorOpen,
  persistActivityTableLayout,
  activityColumnSortFieldById,
  handleActivitySort,
  activitySortField,
  activitySortOrder,
  renderActivityFilterCell,
  paginatedReports,
  getReportStartEnd,
  getReportDuration,
  activityCostByActivityId,
  activityPageStartIndex,
  getAssigneeLabelForExport,
  getReportFormContent,
  getReportSubmitDescription,
  latestManagerCommentsByActivityId,
  getActivityFormColumnValue,
  formatDateOnlyTr,
  formatTimeOnlyTr,
  formatHoursTr,
  formatCurrencyTr,
  t,
  handleApproveActivity,
  handleRejectActivity,
  isAdmin,
  isManager,
  openManagerCommentModal,
  handleViewDetails,
  handleOpenDeleteActivityConfirm,
  activityTabReports,
  activitySummaryMode,
  setActivitySummaryMode,
  filteredActivitiesAverageDuration,
  filteredActivitiesTotalDuration,
  filteredActivitiesAverageCost,
  filteredActivitiesTotalCost,
  activityTotalPages,
  activityCurrentPage,
  setActivityCurrentPage,
  exportActivityTabToExcel,
  activityItemsPerPage,
  setActivityItemsPerPage,
  reports,
}) => {
  const {
    scrollContainerRef,
    setScrollTop,
    virtualizationEnabled,
    startIndex: virtualStartIndex,
    endIndex: virtualEndIndex,
    topSpacerHeight,
    bottomSpacerHeight,
  } = useVirtualizedRows({
    itemCount: paginatedReports.length,
    rowHeight: 46,
    overscan: 10,
    enabled: !loading && paginatedReports.length > 80,
    resetKey: `${activityCurrentPage}-${activityItemsPerPage}-${paginatedReports.length}`,
  });

  const visibleReports = useMemo(
    () =>
      virtualizationEnabled
        ? paginatedReports.slice(virtualStartIndex, virtualEndIndex)
        : paginatedReports,
    [paginatedReports, virtualEndIndex, virtualStartIndex, virtualizationEnabled],
  );

  const labels = useMemo(
    () => ({
      searchPlaceholder: t("dashboard:managerActivities.searchPlaceholder"),
      activity: t("dashboard:managerActivities.activity"),
      employeeName: t("dashboard:managerActivities.employeeName"),
      employee: t("dashboard:managerActivities.employee"),
      tableConfiguratorTitle: t(
        "dashboard:managerActivities.tableConfiguratorTitle",
      ),
      tableConfiguratorDescription: t(
        "dashboard:managerActivities.tableConfiguratorDescription",
      ),
      sort: t("dashboard:managerActivities.sort"),
      loadingData: t("dashboard:managerActivities.loadingData"),
      formField: t("dashboard:managerActivities.formField"),
      jobDetail: t("dashboard:managerActivities.jobDetail"),
      managerComment: t("dashboard:managerActivities.managerComment"),
      comment: t("dashboard:managerActivities.comment"),
      summaryTotal: t("dashboard:managerActivities.summaryTotal"),
      summaryAverage: t("dashboard:managerActivities.summaryAverage"),
      tableEdit: t("dashboard:managerActivities.tableEdit"),
      shortEdit: t("dashboard:managerActivities.shortEdit"),
      excelDownload: t("dashboard:managerActivities.excelDownload"),
      showAll: t("dashboard:managerActivities.showAll"),
      recordCount: (count: number) =>
        t("dashboard:managerActivities.recordCount", { count }),
      recordsMore: (count: number) =>
        t("dashboard:managerActivities.recordsMore", { count }),
      recordsFound: (count: number) =>
        t("dashboard:managerActivities.recordsFound", { count }),
      totalRecordsFrom: (total: number) =>
        t("dashboard:managerActivities.totalRecordsFrom", { total }),
    }),
    [t],
  );
  const statusOptions = useMemo(
    () => [
      { value: "all", label: t("common:status") },
      {
        value: "COMPLETED",
        label: `${t("dashboard:completed")} (${completedCount})`,
      },
      ...(showPendingApprovalStatusOption
        ? [
            {
              value: "PENDING_APPROVAL",
              label: `${t("dashboard:pendingApproval")} (${pendingApprovalCount})`,
            },
          ]
        : []),
      {
        value: "PENDING",
        label: `${t("dashboard:pending")} (${pendingCount})`,
      },
      {
        value: "REJECTED",
        label: `${t("tasks:status.rejected")} (${rejectedCount})`,
      },
      {
        value: taskActivitiesFilter,
        label: t("dashboard:managerActivities.taskActivities", {
          count: taskActivitiesCount,
        }),
      },
    ],
    [
      completedCount,
      pendingApprovalCount,
      pendingCount,
      rejectedCount,
      showPendingApprovalStatusOption,
      t,
      taskActivitiesCount,
      taskActivitiesFilter,
    ],
  );
  const workflowOptions = useMemo(
    () => [
      { value: "all", label: workflowText },
      ...uniqueWorkflows.map((workflow: string) => ({
        value: workflow,
        label: workflow,
      })),
    ],
    [uniqueWorkflows, workflowText],
  );
  const selectedProjectValue = selectedProject
    ? `${selectedProject.workflowId}-${selectedProject.projectName}`
    : "all";
  const projectOptions = useMemo(
    () => [
      { value: "all", label: t("projects:project") },
      ...uniqueProjects.map((project: any) => ({
        value: `${project.workflowId}-${project.projectName}`,
        label: project.projectName,
        searchText: `${project.projectName} ${project.workflowId}`,
      })),
    ],
    [t, uniqueProjects],
  );
  const activityOptions = useMemo(
    () => [
      { value: "all", label: labels.activity },
      ...uniqueActivities.map((activity: string) => ({
        value: activity,
        label: activity,
      })),
    ],
    [labels.activity, uniqueActivities],
  );
  const assigneeOptions = useMemo(
    () => [
      { value: "all", label: labels.employee },
      ...uniqueAssignees.map((assignee: string) => ({
        value: assignee,
        label: assignee,
      })),
    ],
    [labels.employee, uniqueAssignees],
  );

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="relative z-[60] bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-white/20 overflow-visible">
          <div className="bg-slate-50 border-b-2 border-gray-200 px-2.5 py-2 overflow-visible">
            <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:gap-1 sm:overflow-x-auto lg:overflow-visible">
              <div className="relative w-[146px] xl:w-[168px] min-w-[126px] shrink-0">
                <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-indigo-400" />
                <input
                  type="text"
                  placeholder={labels.searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-8 pr-2 py-1.5 text-[13px] bg-white/80 backdrop-blur-sm border-2 border-indigo-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-400 shadow-sm transition-all font-medium"
                />
              </div>
              <div className="hidden lg:block h-8 w-px bg-gradient-to-b from-indigo-200 via-purple-300 to-pink-200 shrink-0" />
              {!isTasksTab && (
                <SearchableFilterSelect
                  value={statusFilter}
                  onChange={setStatusFilter}
                  options={statusOptions}
                  searchPlaceholder={t("common:search")}
                  emptyLabel={t("common:noResults")}
                  title={t("common:status")}
                  containerClassName="w-[120px] xl:w-[132px] min-w-[120px]"
                  buttonClassName="border-emerald-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 font-semibold text-emerald-700 hover:bg-emerald-50"
                />
              )}
              <SearchableFilterSelect
                value={workflowFilter}
                onChange={setWorkflowFilter}
                options={workflowOptions}
                searchPlaceholder={t("common:search")}
                emptyLabel={t("common:noResults")}
                title={workflowText}
                containerClassName="w-[136px] xl:w-[148px] min-w-[136px]"
                buttonClassName="border-purple-200 focus:ring-2 focus:ring-purple-500 focus:border-purple-400 font-semibold text-purple-700 hover:bg-purple-50"
              />
              <SearchableFilterSelect
                value={selectedProjectValue}
                onChange={(value) => {
                  if (value === "all") {
                    setSelectedProject(null);
                    return;
                  }
                  const [workflowId, ...projectNameParts] = value.split("-");
                  setSelectedProject({
                    workflowId: Number.parseInt(workflowId, 10),
                    projectName: projectNameParts.join("-"),
                  });
                }}
                options={projectOptions}
                searchPlaceholder={t("common:search")}
                emptyLabel={t("common:noResults")}
                title={t("projects:project")}
                containerClassName="w-[120px] xl:w-[132px] min-w-[120px]"
                buttonClassName="border-blue-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-400 font-semibold text-blue-700 hover:bg-blue-50"
              />
              <SearchableFilterSelect
                value={activityFilter}
                onChange={setActivityFilter}
                options={activityOptions}
                searchPlaceholder={t("common:search")}
                emptyLabel={t("common:noResults")}
                title={labels.activity}
                containerClassName="w-[124px] xl:w-[136px] min-w-[124px]"
                buttonClassName="border-cyan-200 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-400 font-semibold text-cyan-700 hover:bg-cyan-50"
              />
              <SearchableFilterSelect
                value={assigneeFilter}
                onChange={setAssigneeFilter}
                options={assigneeOptions}
                searchPlaceholder={t("common:search")}
                emptyLabel={t("common:noResults")}
                title={labels.employeeName}
                containerClassName="w-[136px] xl:w-[148px] min-w-[136px]"
                buttonClassName="border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-400 font-semibold text-amber-700 hover:bg-amber-50"
              />
              {useSingleDateFilter ? (
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setStartDate("");
                    setEndDate(e.target.value);
                  }}
                  className="w-[100px] xl:w-[112px] px-1 py-1.5 text-[13px] border-2 border-rose-200 rounded-lg bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-400 shadow-sm font-semibold text-rose-700 transition-all hover:bg-rose-50 shrink-0"
                  placeholder={t("dashboard:date")}
                  title={t("dashboard:date")}
                />
              ) : (
                <>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-[100px] xl:w-[112px] px-1 py-1.5 text-[13px] border-2 border-pink-200 rounded-lg bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-400 shadow-sm font-semibold text-pink-700 transition-all hover:bg-pink-50 shrink-0"
                    placeholder={t("dashboard:startDate")}
                    title={t("dashboard:startDate")}
                  />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-[100px] xl:w-[112px] px-1 py-1.5 text-[13px] border-2 border-rose-200 rounded-lg bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-400 shadow-sm font-semibold text-rose-700 transition-all hover:bg-rose-50 shrink-0"
                    placeholder={t("dashboard:endDate")}
                    title={t("dashboard:endDate")}
                  />
                </>
              )}
              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  className="flex items-center gap-1 px-1.5 py-1.5 text-[13px] border-2 border-red-200 rounded-lg bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-red-500 font-semibold text-red-600 transition-all hover:bg-red-50 shadow-sm whitespace-nowrap shrink-0"
                >
                  <XMarkIcon className="w-4 h-4" />
                  <span className="hidden xl:inline">{t("common:clear")}</span>
                </button>
              )}
            </div>
          </div>
      </div>
      <div
        ref={tableCardRef}
        className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-white/20 overflow-hidden flex flex-col"
        style={tableCardStyle}
      >
        <div
          ref={scrollContainerRef}
          onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
          className="overflow-auto min-h-0 flex-1"
        >
          <div className="relative">
            <ActivityTableConfigurator
              title={labels.tableConfiguratorTitle}
              description={labels.tableConfiguratorDescription}
              isOpen={isActivityTableConfiguratorOpen}
              selectedColumns={activityVisibleColumns}
              defaultColumns={activityDefaultColumns}
              formColumns={activityAvailableFormColumns}
              saving={isSavingActivityTableLayout}
              onClose={() => setIsActivityTableConfiguratorOpen(false)}
              onSave={persistActivityTableLayout}
            />
            <div className="space-y-3 p-3 md:hidden">
              {loading ? (
                <div className="flex items-center justify-center gap-3 py-8 text-sm text-blue-700">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-300 border-t-blue-700" />
                  {labels.loadingData}
                </div>
              ) : paginatedReports.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-200 bg-white p-6 text-center text-sm text-gray-500">
                  {t("dashboard:noActivitiesFound")}
                </div>
              ) : (
                visibleReports.map((report: any, idx: number) => {
                  const { start } = getReportStartEnd(report);
                  const duration = getReportDuration(report);
                  const activityCost =
                    activityCostByActivityId.get(report.activityId) ?? 0;
                  const assigneeLabel = getAssigneeLabelForExport(report);
                  const content = getReportFormContent(report);
                  const description = getReportSubmitDescription(report);
                  const latestComment =
                    latestManagerCommentsByActivityId[String(report.activityId)];
                  const canManageActivity = isAdmin() || isManager();
                  const isPendingApproval =
                    report.activityStatus === "PENDING_APPROVAL" &&
                    canManageActivity;

                  return (
                    <div
                      key={`mobile-${report.activityId}-${idx}`}
                      className={`rounded-lg border px-3 py-2 shadow-sm ${
                        isPendingApproval
                          ? "border-amber-200 bg-amber-50/70"
                          : "border-blue-100 bg-white"
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <span className="mt-0.5 flex h-6 min-w-6 items-center justify-center rounded-md bg-blue-50 text-[11px] font-bold text-blue-700">
                          #{activityPageStartIndex + virtualStartIndex + idx + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleViewDetails(report)}
                          className="min-w-0 flex-1 text-left"
                        >
                          <div className="flex min-w-0 items-center gap-2">
                            <p className="truncate text-[13px] font-bold text-gray-900">
                              {report.activityTitle || "-"}
                            </p>
                            <span
                              className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${
                                isPendingApproval
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-slate-100 text-slate-700"
                              }`}
                            >
                              {report.activityStatus || "-"}
                            </span>
                          </div>
                          <p className="mt-0.5 truncate text-[11px] font-medium text-gray-500">
                            {report.projectName || "-"} - {assigneeLabel || "-"}
                          </p>
                          <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[11px] font-semibold text-slate-600">
                            <span>
                              {formatDateOnlyTr(start)} {formatTimeOnlyTr(start)}
                            </span>
                            <span>
                              {duration > 0 ? formatHoursTr(duration) : "-"}{" "}
                              {t("dashboard:hours")}
                            </span>
                            <span>{formatCurrencyTr(activityCost)}</span>
                          </div>
                          {(content.summary ||
                            description ||
                            latestComment?.comment) && (
                            <p className="mt-1 truncate text-[11px] text-gray-500">
                              {content.summary ||
                                description ||
                                latestComment?.comment}
                            </p>
                          )}
                        </button>
                        <div className="flex shrink-0 items-center gap-1">
                          {canManageActivity && (
                            <button
                              onClick={() => openManagerCommentModal(report)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-emerald-50 text-emerald-700"
                              title={labels.comment}
                            >
                              <DocumentTextIcon className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleViewDetails(report)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-blue-50 text-blue-700"
                            title={t("common:view")}
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          {canManageActivity && (
                            <button
                              onClick={() => handleOpenDeleteActivityConfirm(report)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-red-50 text-red-700"
                              title={t("common:delete")}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      {isPendingApproval && (
                        <div className="mt-2 grid grid-cols-2 gap-2 border-t border-amber-100 pt-2">
                          <button
                            onClick={() => handleApproveActivity(report.activityId)}
                            className="inline-flex h-8 items-center justify-center rounded-md bg-green-600 text-xs font-semibold text-white"
                          >
                            <CheckCircleIcon className="mr-1 h-4 w-4" />
                            {t("common:approve")}
                          </button>
                          <button
                            onClick={() => handleRejectActivity(report.activityId)}
                            className="inline-flex h-8 items-center justify-center rounded-md bg-red-600 text-xs font-semibold text-white"
                          >
                            <XCircleIcon className="mr-1 h-4 w-4" />
                            {t("common:reject")}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            <table className="hidden min-w-full divide-y divide-gray-200 md:table">
              <thead className="bg-gradient-to-r from-blue-100 to-indigo-100 sticky top-0 z-30 border-b border-gray-200">
                <tr>
                  {activityVisibleColumns.map((column: any) => {
                    const sortField =
                      column.kind === "form"
                        ? column.id
                        : activityColumnSortFieldById[column.id];
                    const isSticky =
                      column.kind === "default" && column.id === "rowNo";
                    const isNumeric = isActivityNumericColumn(column);
                    const headerClasses = isSticky
                      ? "px-4 py-2 text-left text-xs font-bold text-blue-800 uppercase tracking-wider sticky top-0 left-0 z-40 bg-gradient-to-r from-blue-100 to-indigo-100 border-r border-blue-200"
                      : `px-4 py-2 ${isNumeric ? "text-right" : "text-left"} text-xs font-bold text-blue-800 uppercase tracking-wider${
                          sortField
                            ? " cursor-pointer hover:bg-blue-200 transition-colors"
                            : ""
                        }`;
                    return (
                      <th
                        key={`activity-header-${column.kind}-${column.id}`}
                        onClick={
                          sortField
                            ? () => handleActivitySort(sortField)
                            : undefined
                        }
                        className={headerClasses}
                        title={sortField ? labels.sort : undefined}
                      >
                        <div
                          className={`flex w-full items-start gap-1 ${
                            isNumeric ? "justify-end" : ""
                          }`}
                        >
                          {renderActivityHeaderLabel(String(column.label))}
                          {sortField && (
                            <SortIcon
                              field={sortField}
                              currentField={activitySortField}
                              currentOrder={activitySortOrder}
                            />
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
                <tr className="bg-blue-50/80 border-t border-blue-200">
                  {activityVisibleColumns.map(renderActivityFilterCell)}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td
                      colSpan={activityVisibleColumns.length}
                      className="px-4 py-10"
                    >
                      <div className="flex items-center justify-center gap-3 text-sm text-blue-700">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-300 border-t-blue-700"></div>
                        {labels.loadingData}
                      </div>
                    </td>
                  </tr>
                ) : paginatedReports.length === 0 ? (
                  <tr>
                    <td
                      colSpan={activityVisibleColumns.length}
                      className="px-4 py-8 text-center text-sm text-gray-500"
                    >
                      {t("dashboard:noActivitiesFound")}
                    </td>
                  </tr>
                ) : null}
                {!loading && virtualizationEnabled && topSpacerHeight > 0 && (
                  <tr aria-hidden="true">
                    <td
                      colSpan={activityVisibleColumns.length}
                      style={{ height: `${topSpacerHeight}px`, padding: 0 }}
                      className="border-0 bg-transparent"
                    />
                  </tr>
                )}
                {!loading &&
                  visibleReports.map((report: any, idx: number) => {
                    const { start, end } = getReportStartEnd(report);
                    const duration = getReportDuration(report);
                    const activityCost =
                      activityCostByActivityId.get(report.activityId) ?? 0;
                    const rowNo =
                      activityPageStartIndex + virtualStartIndex + idx + 1;
                    const assigneeLabel = getAssigneeLabelForExport(report);
                    const content = getReportFormContent(report);
                    const description = getReportSubmitDescription(report);
                    const latestComment =
                      latestManagerCommentsByActivityId[
                        String(report.activityId)
                      ];
                    const activityInstanceName =
                      report.activityInstanceName || report.activityTitle || "";

                    return (
                      <tr
                        key={`${report.activityId}-${idx}`}
                        className="group hover:bg-gradient-to-r hover:from-purple-50/50 hover:to-blue-50/30 transition-all duration-200 border-b border-gray-100"
                      >
                        {activityVisibleColumns.map((column: any) => {
                          if (column.kind === "form") {
                            const value =
                              getActivityFormColumnValue(
                                content.tooltip,
                                column,
                                activityInstanceName,
                              ) || "-";

                            return (
                              <td
                                key={`${report.activityId}-${column.id}`}
                                className="px-4 py-1 text-xs text-gray-800 max-w-[260px]"
                              >
                                <OverflowInfoCell
                                  value={value}
                                  title={String(column.label || labels.formField)}
                                  threshold={28}
                                />
                              </td>
                            );
                          }

                          switch (column.id) {
                            case "rowNo":
                              return (
                                <td
                                  key={`${report.activityId}-${column.id}`}
                                  className="px-4 py-1 whitespace-nowrap text-xs font-semibold text-gray-900 sticky left-0 bg-white border-r border-gray-200 group-hover:bg-purple-50/50"
                                >
                                  {rowNo}
                                </td>
                              );
                            case "endDate":
                              return (
                                <td
                                  key={`${report.activityId}-${column.id}`}
                                  className="px-4 py-1 whitespace-nowrap text-xs text-gray-700 font-medium"
                                >
                                  {formatDateOnlyTr(end)}
                                </td>
                              );
                            case "assignee":
                              return (
                                <td
                                  key={`${report.activityId}-${column.id}`}
                                  className="px-4 py-1 text-xs font-semibold text-gray-900 max-w-[220px]"
                                >
                                  <OverflowInfoCell
                                    value={assigneeLabel}
                                    title={String(column.label || labels.employeeName)}
                                    threshold={24}
                                  />
                                </td>
                              );
                            case "startTime":
                              return (
                                <td
                                  key={`${report.activityId}-${column.id}`}
                                  className="px-4 py-1 whitespace-nowrap text-xs text-gray-700"
                                >
                                  {formatTimeOnlyTr(start)}
                                </td>
                              );
                            case "endTimeOnly":
                              return (
                                <td
                                  key={`${report.activityId}-${column.id}`}
                                  className="px-4 py-1 whitespace-nowrap text-xs text-gray-700"
                                >
                                  {formatTimeOnlyTr(end)}
                                </td>
                              );
                            case "duration":
                              return (
                                <td
                                  key={`${report.activityId}-${column.id}`}
                                  className="px-4 py-1 whitespace-nowrap text-right"
                                >
                                  <div className="flex items-center justify-end space-x-2">
                                    <span className="font-semibold text-gray-900 text-xs">
                                      {duration > 0 ? formatHoursTr(duration) : "-"}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {t("dashboard:hours")}
                                    </span>
                                  </div>
                                </td>
                              );
                            case "cost":
                              return (
                                <td
                                  key={`${report.activityId}-${column.id}`}
                                  className="px-4 py-1 whitespace-nowrap text-right text-xs font-semibold text-emerald-700"
                                >
                                  {formatCurrencyTr(activityCost)}
                                </td>
                              );
                            case "projectName":
                              return (
                                <td
                                  key={`${report.activityId}-${column.id}`}
                                  className="px-4 py-1 text-xs text-gray-900 font-medium max-w-[240px]"
                                >
                                  <OverflowInfoCell
                                    value={report.projectName || "-"}
                                    fullValue={report.projectName || ""}
                                    title={String(column.label || t("projects:project"))}
                                    threshold={28}
                                  />
                                </td>
                              );
                            case "activityTitle":
                              return (
                                <td
                                  key={`${report.activityId}-${column.id}`}
                                  className="px-4 py-1 text-xs text-gray-900 font-medium max-w-[260px]"
                                >
                                  <OverflowInfoCell
                                    value={report.activityTitle || "-"}
                                    fullValue={report.activityTitle || ""}
                                    title={String(column.label || labels.activity)}
                                    threshold={28}
                                  />
                                </td>
                              );
                            case "formContent":
                              return (
                                <td
                                  key={`${report.activityId}-${column.id}`}
                                  className="px-4 py-1 text-xs text-gray-800 font-medium max-w-[520px]"
                                >
                                  <OverflowInfoCell
                                    value={content.summary}
                                    fullValue={content.tooltip || content.summary}
                                    title={labels.jobDetail}
                                    forceShowInfo={
                                      Boolean(content.tooltip?.trim()) &&
                                      content.tooltip.trim() !==
                                        content.summary.trim()
                                    }
                                    threshold={42}
                                  />
                                </td>
                              );
                            case "submitDescription":
                              return (
                                <td
                                  key={`${report.activityId}-${column.id}`}
                                  className="px-4 py-1 text-xs text-gray-700 max-w-xs"
                                >
                                  <OverflowInfoCell
                                    value={description || "-"}
                                    fullValue={description || ""}
                                    title={String(
                                      column.label || labels.jobDetail,
                                    )}
                                    threshold={28}
                                  />
                                </td>
                              );
                            case "managerComment":
                              return (
                                <td
                                  key={`${report.activityId}-${column.id}`}
                                  className="px-4 py-1 text-xs text-gray-700 max-w-xs"
                                >
                                  <OverflowInfoCell
                                    value={latestComment?.comment || "-"}
                                    fullValue={latestComment?.comment || ""}
                                    title={
                                      latestComment?.comment
                                        ? `${latestComment.authorName || ""} ${
                                            latestComment.createdAt
                                              ? "(" +
                                                new Date(
                                                  latestComment.createdAt,
                                                ).toLocaleString() +
                                                ")"
                                              : ""
                                          }`.trim()
                                        : labels.managerComment
                                    }
                                    threshold={28}
                                  />
                                </td>
                              );
                            case "details":
                              return (
                                <td
                                  key={`${report.activityId}-${column.id}`}
                                  className="px-4 py-1 whitespace-nowrap text-xs font-medium"
                                >
                                  <div className="flex items-center gap-2">
                                    {report.activityStatus ===
                                      "PENDING_APPROVAL" &&
                                      (isAdmin() || isManager()) && (
                                      <>
                                        <button
                                          onClick={() =>
                                            handleApproveActivity(report.activityId)
                                          }
                                          className="flex items-center px-3 py-1 bg-green-600 text-white hover:bg-green-700 rounded-lg transition-all duration-200 font-semibold shadow-sm"
                                        >
                                          <CheckCircleIcon className="h-4 w-4 mr-1.5" />
                                          {t("common:approve")}
                                        </button>
                                        <button
                                          onClick={() =>
                                            handleRejectActivity(report.activityId)
                                          }
                                          className="flex items-center px-3 py-1 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-all duration-200 font-semibold shadow-sm"
                                        >
                                          <XCircleIcon className="h-4 w-4 mr-1.5" />
                                          {t("common:reject")}
                                        </button>
                                      </>
                                    )}
                                    {(isAdmin() || isManager()) && (
                                      <button
                                        onClick={() =>
                                          openManagerCommentModal(report)
                                        }
                                        className="flex items-center px-3 py-1 bg-emerald-600 text-white hover:bg-emerald-700 rounded-lg transition-all duration-200 font-semibold shadow-sm"
                                      >
                                        <DocumentTextIcon className="h-4 w-4 mr-1.5" />
                                        {labels.comment}
                                      </button>
                                    )}
                                    <button
                                      onClick={() => handleViewDetails(report)}
                                      className="flex items-center px-3 py-1 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-all duration-200 font-semibold shadow-sm"
                                    >
                                      <EyeIcon className="h-4 w-4 mr-1.5" />
                                      {t("common:view")}
                                    </button>
                                    {(isAdmin() || isManager()) && (
                                      <button
                                        onClick={() =>
                                          handleOpenDeleteActivityConfirm(report)
                                        }
                                        className="flex items-center px-3 py-1 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-all duration-200 font-semibold shadow-sm"
                                        title={t("common:delete")}
                                      >
                                        <TrashIcon className="h-4 w-4 mr-1.5" />
                                        {t("common:delete")}
                                      </button>
                                    )}
                                  </div>
                                </td>
                              );
                            default:
                              return null;
                          }
                        })}
                      </tr>
                    );
                  })}
                {!loading && virtualizationEnabled && bottomSpacerHeight > 0 && (
                  <tr aria-hidden="true">
                    <td
                      colSpan={activityVisibleColumns.length}
                      style={{ height: `${bottomSpacerHeight}px`, padding: 0 }}
                      className="border-0 bg-transparent"
                    />
                  </tr>
                )}
                {!loading && activityTabReports.length > paginatedReports.length && (
                  <tr className="bg-blue-100/70 border-y-2 border-blue-200">
                    <td className="px-4 py-3 text-sm font-bold text-blue-800 sticky left-0 bg-blue-100/80 border-r border-blue-200">
                      ...
                    </td>
                    <td
                      colSpan={Math.max(activityVisibleColumns.length - 1, 1)}
                      className="px-4 py-3 text-sm font-bold text-blue-800"
                    >
                      ...{" "}
                      {labels.recordsMore(
                        activityTabReports.length - paginatedReports.length,
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot className="sticky bottom-0 z-20 bg-blue-50/95 backdrop-blur border-t-2 border-blue-200">
                <tr>
                  {activityVisibleColumns.map((column: any, index: number) => {
                    if (column.kind === "form") {
                      return (
                        <td
                          key={`activity-summary-${column.id}-${index}`}
                          className="px-4 py-2 text-xs text-gray-700"
                        >
                          {index === activityVisibleColumns.length - 1
                            ? labels.recordCount(activityTabReports.length)
                            : ""}
                        </td>
                      );
                    }

                    if (column.id === "rowNo") {
                      return (
                        <td
                          key={`activity-summary-${column.id}`}
                          className="sticky left-0 z-30 px-4 py-2 text-xs font-bold text-blue-900 bg-blue-50/95 border-r border-blue-200"
                        >
                          <div className="flex items-center">
                            <div className="inline-flex flex-none rounded-md border border-blue-200 overflow-hidden">
                              <button
                                type="button"
                                onClick={() => setActivitySummaryMode("total")}
                                className={`px-2 py-0.5 text-[10px] font-semibold shrink-0 whitespace-nowrap ${
                                  activitySummaryMode === "total"
                                    ? "bg-blue-600 text-white"
                                    : "bg-white text-blue-700 hover:bg-blue-100"
                                }`}
                              >
                                {labels.summaryTotal}
                              </button>
                              <button
                                type="button"
                                onClick={() => setActivitySummaryMode("average")}
                                className={`px-2 py-0.5 text-[10px] font-semibold border-l border-blue-200 shrink-0 whitespace-nowrap ${
                                  activitySummaryMode === "average"
                                    ? "bg-blue-600 text-white"
                                    : "bg-white text-blue-700 hover:bg-blue-100"
                                }`}
                              >
                                {labels.summaryAverage}
                              </button>
                            </div>
                          </div>
                        </td>
                      );
                    }

                    if (column.id === "duration") {
                      return (
                        <td
                          key={`activity-summary-${column.id}`}
                          className="px-4 py-2 whitespace-nowrap text-right"
                        >
                          <div className="flex items-center justify-end space-x-2">
                            <span className="font-bold text-gray-900 text-xs">
                              {(activitySummaryMode === "average"
                                ? filteredActivitiesAverageDuration
                                : filteredActivitiesTotalDuration) > 0
                                ? formatHoursTr(
                                    activitySummaryMode === "average"
                                      ? filteredActivitiesAverageDuration
                                      : filteredActivitiesTotalDuration,
                                  )
                                : "-"}
                            </span>
                            <span className="text-xs text-gray-600">
                              {t("dashboard:hours")}
                            </span>
                          </div>
                        </td>
                      );
                    }

                    if (column.id === "cost") {
                      return (
                        <td
                          key={`activity-summary-${column.id}`}
                          className="px-4 py-2 whitespace-nowrap text-right text-xs font-bold text-emerald-700"
                        >
                          {formatCurrencyTr(
                            activitySummaryMode === "average"
                              ? filteredActivitiesAverageCost
                              : filteredActivitiesTotalCost,
                          )}
                        </td>
                      );
                    }

                    return (
                    <td
                      key={`activity-summary-${column.id}`}
                      className="px-4 py-2 text-xs text-gray-700 font-medium"
                    >
                      {index === activityVisibleColumns.length - 1
                          ? labels.recordCount(activityTabReports.length)
                          : ""}
                    </td>
                  );
                  })}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
        {activityTabReports.length > 0 && (
          <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
            <div className="flex flex-col md:flex-row items-center justify-between gap-1.5">
              <div className="text-xs text-gray-600">
                {labels.recordsFound(activityTabReports.length)}
                {hasActiveFilters && (
                  <span className="text-gray-400">
                    {" "}
                    {labels.totalRecordsFrom(reports.length)}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-1.5">
                {activityTotalPages > 1 && (
                  <>
                    <button
                      onClick={() =>
                        setActivityCurrentPage(activityCurrentPage - 1)
                      }
                      disabled={activityCurrentPage === 1}
                      className="px-2.5 py-1.5 text-xs border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeftIcon className="w-4 h-4" />
                    </button>

                    <div className="flex items-center gap-1">
                      {Array.from(
                        { length: activityTotalPages },
                        (_, i) => i + 1,
                      )
                        .filter((page) => {
                          return (
                            page === 1 ||
                            page === activityTotalPages ||
                            Math.abs(page - activityCurrentPage) <= 1
                          );
                        })
                                .map((page: number, idx: number, arr: number[]) => (
                          <React.Fragment key={page}>
                            {idx > 0 && arr[idx - 1] !== page - 1 && (
                              <span className="px-2 text-gray-400">...</span>
                            )}
                            <button
                              onClick={() => setActivityCurrentPage(page)}
                              className={`px-2.5 py-1.5 text-xs rounded-md ${
                                activityCurrentPage === page
                                  ? "bg-blue-600 text-white font-medium"
                                  : "border border-gray-300 hover:bg-gray-100"
                              }`}
                            >
                              {page}
                            </button>
                          </React.Fragment>
                        ))}
                    </div>

                    <button
                      onClick={() =>
                        setActivityCurrentPage(activityCurrentPage + 1)
                      }
                      disabled={activityCurrentPage === activityTotalPages}
                      className="px-2.5 py-1.5 text-xs border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRightIcon className="w-4 h-4" />
                    </button>
                  </>
                )}

                <div
                  className={`${
                    activityTotalPages > 1 ? "ml-2" : ""
                  } flex items-center gap-1.5`}
                >
                  <button
                    type="button"
                    onClick={() =>
                      setIsActivityTableConfiguratorOpen((prev: boolean) => !prev)
                    }
                    className="inline-flex items-center h-7 px-2.5 py-1 text-xs font-bold rounded-md shadow-md border border-blue-200 bg-white text-blue-800 transition whitespace-nowrap shrink-0 hover:bg-blue-50"
                    title={labels.tableEdit}
                  >
                    <Cog6ToothIcon className="w-4 h-4 mr-1.5" />
                    <span className="hidden xl:inline">{labels.tableEdit}</span>
                    <span className="xl:hidden">{labels.shortEdit}</span>
                  </button>
                  <button
                    type="button"
                    onClick={exportActivityTabToExcel}
                    disabled={activityTabReports.length === 0}
                    className={`inline-flex items-center h-7 px-2.5 py-1 text-xs font-bold rounded-md shadow-md border border-transparent text-white transition whitespace-nowrap shrink-0 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:ring-indigo-500 ${
                      activityTabReports.length === 0
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:shadow-lg"
                    } focus:outline-none focus:ring-2 focus:ring-offset-2`}
                    title={labels.excelDownload}
                  >
                    <DocumentTextIcon className="w-4 h-4 mr-1.5" />
                    <span className="hidden xl:inline">{labels.excelDownload}</span>
                    <span className="xl:hidden">Excel</span>
                  </button>

                  <select
                    value={activityItemsPerPage}
                    onChange={(e) => {
                      setActivityItemsPerPage(Number(e.target.value));
                      setActivityCurrentPage(1);
                    }}
                    className="h-7 px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={200}>200 {t("common:itemsPerPage")}</option>
                    <option value={500}>500 {t("common:itemsPerPage")}</option>
                    <option value={1000}>1000 {t("common:itemsPerPage")}</option>
                    <option value={-1}>{labels.showAll}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

